import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import useRebalanceBasketPreview from '@/hooks/use-rebalance-basket-preview'
import { chainIdAtom } from '@/state/atoms'
import {
  indexDTFAtom,
  indexDTFBasketAtom,
  indexDTFBasketPricesAtom,
  indexDTFBasketSharesAtom,
  isSingletonRebalanceAtom,
} from '@/state/dtf/atoms'
import { ExplorerDataType, getExplorerLink } from '@/utils/getExplorerLink'
import { useAtomValue } from 'jotai'
import { ArrowUpRightIcon } from 'lucide-react'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Address, Hex } from 'viem'
import { Trans } from '@lingui/react/macro'
import GovernanceProposalPreviewSkeleton from '../governance-proposal-preview-skeleton'
import { RawCallsPreview } from '../raw-call-preview'
import BasketProposalPreview from './legacy-basket-proposal-preview'
import RebalanceBasketPreview, {
  EstimatedBasket,
} from './rebalance-basket-preview'

const TABS = {
  SUMMARY: 'summary',
  RAW: 'raw',
}

const Header = ({ address }: { address: Address }) => {
  const chainId = useAtomValue(chainIdAtom)

  return (
    <div className="mx-4 py-4 flex items-center flex-wrap gap-2 border-b">
      <h1 className="text-xl font-semibold text-primary">
        <Trans>Basket Change</Trans>
      </h1>
      <Link
        target="_blank"
        className="mr-auto"
        to={getExplorerLink(address, chainId, ExplorerDataType.ADDRESS)}
      >
        <Button
          size="icon-rounded"
          className="bg-primary/10 text-primary h-6 w-6 p-0 hover:text-white"
        >
          <ArrowUpRightIcon size={18} strokeWidth={1.5} />
        </Button>
      </Link>

      <TabsList>
        <TabsTrigger value={TABS.SUMMARY}>
          <Trans>Summary</Trans>
        </TabsTrigger>

        <TabsTrigger value={TABS.RAW}>
          <Trans>Raw</Trans>
        </TabsTrigger>
      </TabsList>
    </div>
  )
}

const RebalancePreview = ({
  calldatas,
  timestamp,
}: {
  calldatas: Hex[] | undefined
  timestamp?: number
}) => {
  const dtf = useAtomValue(indexDTFAtom)
  const { preview: rebalanceBasketPreview, snapshotUnavailable } =
    useRebalanceBasketPreview(calldatas, timestamp)

  const basket = useMemo(() => {
    if (!rebalanceBasketPreview) return {}

    return Object.values(rebalanceBasketPreview.basket).reduce((acc, asset) => {
      acc[asset.token.address] = {
        token: asset.token,
        currentShares: asset.currentWeight,
        targetShares: asset.targetWeight,
        delta: asset.deltaWeight,
        isRemoved: asset.targetWeightRaw === 0n,
      }
      return acc
    }, {} as EstimatedBasket)
  }, [rebalanceBasketPreview])

  if (snapshotUnavailable)
    return (
      <div
        className="rounded-3xl border p-6 text-center text-legend"
        data-testid="rebalance-preview-unavailable"
      >
        <Trans>
          Basket snapshot unavailable — historical data for this proposal's
          rebalance window is no longer served.
        </Trans>
      </div>
    )

  if (!dtf || !rebalanceBasketPreview)
    return <GovernanceProposalPreviewSkeleton />

  return (
    <Tabs
      defaultValue={TABS.SUMMARY}
      className="flex flex-col gap-4 rounded-3xl m-1 bg-background"
    >
      <Header address={dtf.id.toLowerCase() as Address} />
      <TabsContent className="m-0" value={TABS.SUMMARY}>
        <RebalanceBasketPreview basket={basket} />
      </TabsContent>
      <TabsContent className="m-0" value={TABS.RAW}>
        <RawCallsPreview calls={[rebalanceBasketPreview.decodedCalldata]} />
      </TabsContent>
    </Tabs>
  )
}

const LegacyRebalancePreview = ({
  calldatas,
}: {
  calldatas: Hex[] | undefined
}) => {
  const dtf = useAtomValue(indexDTFAtom)
  const basket = useAtomValue(indexDTFBasketAtom)
  const shares = useAtomValue(indexDTFBasketSharesAtom)
  const prices = useAtomValue(indexDTFBasketPricesAtom)

  if (!dtf || !basket || !prices || !calldatas?.length)
    return <GovernanceProposalPreviewSkeleton />

  return (
    <BasketProposalPreview
      calldatas={calldatas}
      basket={basket}
      shares={shares}
      prices={prices}
      address={dtf.id.toLowerCase() as Address}
    />
  )
}

export default ({
  calldatas,
  timestamp,
}: {
  calldatas: Hex[] | undefined
  timestamp?: number
}) => {
  const isSingletonRebalance = useAtomValue(isSingletonRebalanceAtom)

  if (!isSingletonRebalance) {
    return <LegacyRebalancePreview calldatas={calldatas} />
  }

  return <RebalancePreview calldatas={calldatas} timestamp={timestamp} />
}
